import { useEffect, useState } from 'react'
import {
  adoptStoredAppSession,
  dropRejectedRefreshToken,
  getCurrentAuthState,
  setAppSessionJwts,
  setSession,
} from '~/atoms/AuthenticationState'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import {
  isSessionExpiredDialogMounted,
  useSetSessionExpired,
} from '~/atoms/SessionExpiredState'
import { addBreadCrumb } from '~/services/sentry'
import { Environment } from '~/types/EnvironmentTypes'
import {
  ApiClientAuthData,
  AuthenticationResultCallback,
  AuthenticationResultCallbackData,
  AuthenticationType,
  GetAuthDataCallback,
} from '~/types/MangoApiClientTypes'
import { appEnvironment, getApiEnvironment } from '~/utils/common'
import MangoApiClient from '~/utils/mangoApiClient/mangoApiClient'

import { createMangoApiClient } from '@mangovoice/mango-api-client'
import type { MangoApiClient as MangoApiClientType } from '@mangovoice/mango-api-client'
import { AppSessionJwts, SessionTokenData } from '@mangovoice/types'

// //api data transform from state dependency
const getAuthData: GetAuthDataCallback = (): ApiClientAuthData => {
  const currentState = getCurrentAuthState()
  return {
    validationToken: currentState.validationToken?.token ?? null,
    accessToken: currentState.appSessionJwts?.access_token ?? null,
    refreshToken: currentState.appSessionJwts?.refresh_token ?? null,
    sessionToken: currentState.session?.token.key ?? null,
  }
}

let onAuthenticationResultCallback: AuthenticationResultCallback | undefined
/**
 * Exported so secondary transports (e.g. the api-gateway one) can report auth
 * failures through the same path as the mango-api client, rather than each
 * transport reaching its own conclusion about session state.
 */
export const onAuthenticationResult: AuthenticationResultCallback = (
  data: AuthenticationResultCallbackData,
) => {
  addBreadCrumb({
    category: 'api-auth',
    message: 'AUTH_RESULT_CALLBACK_INVOKED',
    data: {
      responseData: data,
    },
  })

  if (data.ok) {
    //success, set the new authentication data
    //validation token will never be set here, it is only set by the login page
    switch (data.authenticationType) {
      case AuthenticationType.GetAppSessionJwts:
      case AuthenticationType.RefreshAppSessionJwts:
        setAppSessionJwts(data.result as AppSessionJwts)
        break
      case AuthenticationType.GetSession:
        setSession(data.result as SessionTokenData)
        break
      default:
        break
    }
  }

  onAuthenticationResultCallback?.(data)
}

let apiEnv = getApiEnvironment()
let apiInst: ReturnType<typeof MangoApiClient.create> = MangoApiClient.create()
let mangoApiClientInst: MangoApiClientType | undefined

export const getApiInst = () => {
  //if the current node environment is production, use the production api only
  if (appEnvironment === Environment.PRODUCTION) {
    return apiInst
  }

  //if we're in dev or anything else, consider the api_env in ls.
  const currentApiEnv = getApiEnvironment()
  if (apiEnv !== currentApiEnv) {
    apiEnv = currentApiEnv
    apiInst = MangoApiClient.create()
    mangoApiClientInst = undefined
  }

  return apiInst
}

export const getMangoApiClient = (): MangoApiClientType => {
  if (!mangoApiClientInst) {
    const transport = getApiInst().getHttpTransport()
    mangoApiClientInst = createMangoApiClient(transport)
  }
  return mangoApiClientInst
}

apiInst.setCallbacks(getAuthData, onAuthenticationResult)

/**
 * A refresh that failed without a 401 leaves the refresh token usable, so a
 * later 401 tries it again, until the failures repeat for too long.
 */
const isTransientRefreshFailure = (data: AuthenticationResultCallbackData) =>
  data.authenticationType === AuthenticationType.RefreshAppSessionJwts &&
  !getApiInst().isRefreshTokenRejected() &&
  !getApiInst().isRefreshFailingRepeatedly()

const useMangoApi = () => {
  const { logout } = useAuthenticationState()
  const setSessionExpired = useSetSessionExpired()
  const [loggingOut, setLoggingOut] = useState(false)

  onAuthenticationResultCallback = (data: AuthenticationResultCallbackData) => {
    if (data.ok) return
    if (isTransientRefreshFailure(data)) return

    setLoggingOut(true)
  }

  useEffect(() => {
    if (!loggingOut) return
    setLoggingOut(false)
    if (isSessionExpiredDialogMounted()) {
      if (adoptStoredAppSession()) {
        setSessionExpired(false)
        return
      }
      if (getApiInst().isRefreshTokenRejected()) {
        dropRejectedRefreshToken()
      }
      setSessionExpired(true)
      return
    }
    logout()
  }, [loggingOut])

  return getApiInst()
}

const useMangoApiClient = (): MangoApiClientType => {
  useMangoApi()
  return getMangoApiClient()
}

export { useMangoApi, useMangoApiClient }
